import { Availability, AvailabilityRequest, CheckAvailabilityRequest, AvailabilityCheckResponse, AvailableQuantityResponse } from '../types/availability';
import { apiClient } from './apiClient';

/** Path segment encoding for item IDs that may contain reserved URL characters. */
function encPath(s: string): string {
  return encodeURIComponent(String(s));
}

/**
 * Normalize any JSON shape for a calendar date into yyyy-MM-dd.
 * Handles ISO strings, Java LocalDate arrays, and object shapes some clients/proxies emit.
 */
export function coerceAvailabilityDateKey(raw: unknown): string {
  if (raw == null) return '';
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    const m = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
    const t = Date.parse(trimmed);
    if (!Number.isNaN(t)) {
      const d = new Date(t);
      const y = d.getUTCFullYear();
      const mo = d.getUTCMonth() + 1;
      const da = d.getUTCDate();
      return `${y}-${String(mo).padStart(2, '0')}-${String(da).padStart(2, '0')}`;
    }
    return '';
  }
  if (Array.isArray(raw) && raw.length >= 3) {
    const y = Number(raw[0]);
    const mo = Number(raw[1]);
    const da = Number(raw[2]);
    if (![y, mo, da].every((n) => Number.isFinite(n))) return '';
    return `${y}-${String(mo).padStart(2, '0')}-${String(da).padStart(2, '0')}`;
  }
  if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
    const o = raw as Record<string, unknown>;
    const y = Number(o.year ?? o.y);
    const mo = Number(o.monthValue ?? o.m);
    const da = Number(o.dayOfMonth ?? o.d);
    if ([y, mo, da].every((n) => Number.isFinite(n)) && mo >= 1 && mo <= 12 && da >= 1 && da <= 31) {
      return `${y}-${String(mo).padStart(2, '0')}-${String(da).padStart(2, '0')}`;
    }
  }
  return '';
}

function normalizeAvailability(a: Availability): Availability {
  const raw = (a as unknown as { availabilityDate?: unknown }).availabilityDate;
  const d = coerceAvailabilityDateKey(raw);
  if (!d) return a;
  return { ...a, availabilityDate: d };
}

class AvailabilityService {
  /**
   * Create or update availability for an item
   */
  async createOrUpdateAvailability(request: AvailabilityRequest): Promise<Availability> {
    try {
      const payload: AvailabilityRequest = {
        ...request,
        itemId: String(request.itemId),
        businessId: String(request.businessId),
      };
      const { data } = await apiClient.post<Availability>('/availability', payload);
      const saved = normalizeAvailability(data);
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.debug('[availability] saved', {
          itemId: request.itemId,
          itemType: request.itemType,
          date: request.availabilityDate,
          availabilityId: saved.availabilityId,
        });
      }
      return saved;
    } catch (error: any) {
      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to create/update availability';
      throw new Error(typeof msg === 'string' ? msg : 'Failed to create/update availability');
    }
  }

  /**
   * Get availability for an item on a specific date
   */
  async getAvailability(itemId: string, itemType: string, date: string): Promise<Availability | null> {
    try {
      const { data } = await apiClient.get<Availability>(
        `/availability/item/${encPath(itemId)}/type/${encPath(itemType)}/date/${encPath(date)}`
      );
      return normalizeAvailability(data);
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return null;
      }
      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to get availability';
      throw new Error(typeof msg === 'string' ? msg : 'Failed to get availability');
    }
  }

  /**
   * Get all availabilities for an item
   */
  async getAvailabilitiesForItem(itemId: string, itemType: string): Promise<Availability[]> {
    try {
      const { data } = await apiClient.get<Availability[]>(
        `/availability/item/${encPath(itemId)}/type/${encPath(itemType)}`
      );
      const rows = Array.isArray(data) ? data : [];
      const normalized = rows.map(normalizeAvailability);
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.debug('[availability] loaded', { itemId, itemType, count: normalized.length });
      }
      return normalized;
    } catch (error: any) {
      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to get availabilities';
      throw new Error(typeof msg === 'string' ? msg : 'Failed to get availabilities');
    }
  }

  /**
   * Get availabilities for an item within a date range
   */
  async getAvailabilitiesInRange(
    itemId: string,
    itemType: string,
    startDate: string,
    endDate: string
  ): Promise<Availability[]> {
    const { data } = await apiClient.get<Availability[]>(
      `/availability/item/${encPath(itemId)}/type/${encPath(itemType)}/range`,
      { params: { startDate, endDate } }
    );
    const rows = Array.isArray(data) ? data : [];
    return rows.map(normalizeAvailability);
  }

  /**
   * Get all availabilities for a business
   */
  async getAvailabilitiesForBusiness(businessId: string): Promise<Availability[]> {
    const { data } = await apiClient.get<Availability[]>(`/availability/business/${encPath(businessId)}`);
    const rows = Array.isArray(data) ? data : [];
    return rows.map(normalizeAvailability);
  }

  /**
   * Check if an item is available on a specific date
   */
  async checkAvailability(request: CheckAvailabilityRequest): Promise<boolean> {
    const { data } = await apiClient.post<AvailabilityCheckResponse>('/availability/check', request);
    return data.isAvailable;
  }

  /**
   * Get available quantity for an item on a specific date
   */
  async getAvailableQuantity(itemId: string, itemType: string, date: string): Promise<number> {
    const { data } = await apiClient.get<AvailableQuantityResponse>(
      `/availability/item/${encPath(itemId)}/type/${encPath(itemType)}/date/${encPath(date)}/quantity`
    );
    return data.availableQuantity;
  }

  /**
   * Delete availability for an item on a specific date
   */
  async deleteAvailability(itemId: string, itemType: string, date: string): Promise<void> {
    await apiClient.delete(
      `/availability/item/${encPath(itemId)}/type/${encPath(itemType)}/date/${encPath(date)}`
    );
  }

  /**
   * Delete all availabilities for an item
   */
  async deleteAllAvailabilitiesForItem(itemId: string, itemType: string): Promise<void> {
    await apiClient.delete(`/availability/item/${encPath(itemId)}/type/${encPath(itemType)}`);
  }
}

const availabilityService = new AvailabilityService();
export default availabilityService;
